// Oversætter hele medarbejder-fladen (kontor og steamer) til det valgte sprog – ikke kun menuerne.
// Den lytter på DOM'en og skifter danske tekster ud med ordbogens oversættelse, efterhånden som de vises.
// Undtaget: kundesider (.kundeflade), brugerdata markeret med data-ikke-oversaet (chatbeskeder, navne, kommentarer)
// samt felter man skriver i. Datoer (ugedage og måneder) oversættes med browserens egen kalender.
import type { Sprog } from '../core/typer';

const IDX: Record<Exclude<Sprog, 'da'>, number> = { en: 0, pl: 1, uk: 2, ar: 3, fa: 4 };
const LOKALE: Record<Exclude<Sprog, 'da'>, string> = { en: 'en-GB', pl: 'pl-PL', uk: 'uk-UA', ar: 'ar', fa: 'fa-IR' };
const ATTR = ['placeholder', 'aria-label', 'title', 'alt'] as const;

type Skabelon = { re: RegExp; noegle: string; v: string[] };
let ord: Map<string, string[]> | null = null;
let skabeloner: { efterStart: Map<string, Skabelon[]>; oevrige: Skabelon[] } = { efterStart: new Map(), oevrige: [] };
let henter: Promise<void> | null = null;
let ekstra: Record<string, string[]> = {};

/** Ordbogen er pakket (gzip + base64) og ligger i sin egen fil: den hentes og pakkes først ud, når nogen vælger et andet sprog end dansk. */
export function hentOrdbog(): Promise<void> {
  if (ord) return Promise.resolve();
  if (henter) return henter;
  henter = (async () => {
    let data: Record<string, string[] | null> = {};
    try {
      const { PAKKE } = await import('./ordbog-pakke');
      const bin = Uint8Array.from(atob(PAKKE), (c) => c.charCodeAt(0));
      const ds = new DecompressionStream('gzip');
      const tekst = await new Response(new Blob([bin]).stream().pipeThrough(ds)).text();
      data = JSON.parse(tekst);
    } catch { /* gammel browser: kun den lille indbyggede ordbog bruges */ }
    const m = new Map<string, string[]>();
    for (const [k, v] of Object.entries({ ...data, ...ekstra })) if (v) m.set(k, v);
    ord = m;
    byggSkabeloner();
  })();
  return henter;
}
/** Den lille ordbog i sprog.ts (menuer m.m.) lægges oveni, så alt findes ét sted. */
export function tilfoejOrd(o: Record<string, string[]>) { ekstra = { ...ekstra, ...o }; if (ord) { for (const [k, v] of Object.entries(o)) ord.set(k, v); byggSkabeloner(); } }

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
function byggSkabeloner() {
  skabeloner = { efterStart: new Map(), oevrige: [] };
  for (const [k, v] of ord!) {
    if (!/\{\d\}/.test(k)) continue;
    const dele = k.split(/(\{\d\})/);
    const re = new RegExp('^' + dele.map((d) => (/^\{\d\}$/.test(d) ? '(.+?)' : esc(d))).join('') + '$', 's');
    const s: Skabelon = { re, noegle: k, v };
    const start = dele[0];
    if (start.length >= 3) { const n = start.slice(0, 3); (skabeloner.efterStart.get(n) ?? skabeloner.efterStart.set(n, []).get(n)!).push(s); }
    else skabeloner.oevrige.push(s);
  }
}

// ---------- Datoer: ugedage og måneder via Intl ----------
const datoOrd = new Map<Sprog, Map<string, string>>();
function datoKort(s: Exclude<Sprog, 'da'>): Map<string, string> {
  let m = datoOrd.get(s); if (m) return m;
  m = new Map();
  const par = (o: Intl.DateTimeFormatOptions, d: Date) => [new Intl.DateTimeFormat('da-DK', { ...o, timeZone: 'UTC' }).format(d), new Intl.DateTimeFormat(LOKALE[s], { ...o, timeZone: 'UTC' }).format(d)];
  for (let i = 0; i < 7; i++) { const d = new Date(Date.UTC(2024, 0, 1 + i)); for (const w of ['long', 'short'] as const) { const [a, b] = par({ weekday: w }, d); m.set(a, b); m.set(a.replace(/\.$/, ''), b); } }
  for (let i = 0; i < 12; i++) { const d = new Date(Date.UTC(2024, i, 15)); for (const w of ['long', 'short'] as const) { const [a, b] = par({ month: w }, d); m.set(a, b); m.set(a.replace(/\.$/, ''), b); } }
  datoOrd.set(s, m);
  return m;
}
const DATO_RE = /(^|[\s(])(mandag|tirsdag|onsdag|torsdag|fredag|lørdag|søndag|man\.|tirs\.|ons\.|tors\.|fre\.|lør\.|søn\.|januar|februar|marts|april|maj|juni|juli|august|september|oktober|november|december|jan\.|feb\.|mar\.|apr\.|jun\.|jul\.|aug\.|sep\.|sept\.|okt\.|nov\.|dec\.)(?=$|[\s,.)\d])/g;

/** Oversætter én tekst (uden kanter af mellemrum). Returnerer null, hvis der ikke findes en oversættelse. */
export function oversaetTekst(tekst: string, s: Sprog): string | null {
  if (s === 'da' || !ord) return null;
  const i = IDX[s];
  const direkte = ord.get(tekst);
  if (direkte) return direkte[i] ?? null;
  // Skabeloner med {0}
  const kand = [...(skabeloner.efterStart.get(tekst.slice(0, 3)) ?? []), ...skabeloner.oevrige];
  for (const k of kand) {
    const m = k.re.exec(tekst);
    if (!m) continue;
    let ud = k.v[i]; if (!ud) return null;
    m.slice(1).forEach((vaerdi, n) => { ud = ud.split(`{${n}}`).join(oversaetTekst(vaerdi.trim(), s) ?? datoer(vaerdi, s) ?? vaerdi); });
    return ud;
  }
  // " · "-sammensatte tekster: oversæt hver del
  if (tekst.includes(' · ')) {
    const dele = tekst.split(' · ');
    const ov = dele.map((d) => oversaetTekst(d, s) ?? datoer(d, s));
    if (ov.some((x) => x != null)) return dele.map((d, n) => ov[n] ?? d).join(' · ');
  }
  // "Etiket (3)" og "3 etiket"
  const tal = /^(.+?) \((\d+)\)$/.exec(tekst) ?? null;
  if (tal) { const x = oversaetTekst(tal[1], s); if (x) return `${x} (${tal[2]})`; }
  const foran = /^(\d+) (.+)$/.exec(tekst);
  if (foran) { const x = ord.get(foran[2])?.[i]; if (x) return `${foran[1]} ${x}`; }
  // "Etiket: værdi"
  const kolon = /^([^:]{2,40}): (.+)$/.exec(tekst);
  if (kolon) { const a = ord.get(kolon[1]); if (a?.[i]) return `${a[i]}: ${oversaetTekst(kolon[2], s) ?? datoer(kolon[2], s) ?? kolon[2]}`; }
  return datoer(tekst, s);
}
function datoer(tekst: string, s: Sprog): string | null {
  if (s === 'da') return null;
  DATO_RE.lastIndex = 0;
  if (!DATO_RE.test(tekst)) return null;
  const m = datoKort(s);
  DATO_RE.lastIndex = 0;
  let ud = tekst.replace(DATO_RE, (_hel, foer: string, w: string) => foer + (m.get(w) ?? m.get(w.replace(/\.$/, '')) ?? w));
  ud = ud.replace(/\bkl\. /g, '').replace(/(\d)\. /g, '$1 ');
  const ii = IDX[s]; const idag = ord?.get('I dag')?.[ii], igaar = ord?.get('I går')?.[ii];
  if (idag) ud = ud.replace(/\bi dag\b/gi, idag); if (igaar) ud = ud.replace(/\bi går\b/gi, igaar);
  return ud === tekst ? null : ud;
}

// ---------- DOM ----------
type Spor = { orig: string; vist: string };
const tekstSpor = new WeakMap<Text, Spor>();
const attrSpor = new WeakMap<Element, Record<string, Spor>>();
let sprog: Sprog = 'da';
let observer: MutationObserver | null = null;

function undtaget(el: Element | null): boolean {
  for (let e = el; e; e = e.parentElement) {
    if (e.hasAttribute('data-ikke-oversaet') || e.classList.contains('kundeflade') || e.classList.contains('demo-bjaelke')) return true;
    const t = e.tagName;
    if (t === 'SCRIPT' || t === 'STYLE' || t === 'TEXTAREA' || t === 'CODE' || t === 'svg' || t === 'SVG' || (e as HTMLElement).isContentEditable) return true;
  }
  return false;
}
function behandlTekst(n: Text) {
  const v = n.nodeValue ?? '';
  const spor = tekstSpor.get(n);
  let orig = v;
  if (spor && v === spor.vist) orig = spor.orig; // vores egen ændring
  const trim = orig.trim();
  if (!trim || !/\p{L}{2}/u.test(trim)) { if (spor && spor.vist !== orig) { tekstSpor.delete(n); } return; }
  const ov = sprog === 'da' ? null : oversaetTekst(trim, sprog);
  const ny = ov == null ? orig : orig.replace(trim, ov);
  if (ny !== v) n.nodeValue = ny;
  if (ny !== orig || spor) tekstSpor.set(n, { orig, vist: ny });
}
function behandlAttr(el: Element) {
  for (const a of ATTR) {
    const v = el.getAttribute(a); if (v == null) continue;
    const alle = attrSpor.get(el) ?? {};
    const spor = alle[a];
    const orig = spor && v === spor.vist ? spor.orig : v;
    const ov = sprog === 'da' ? null : oversaetTekst(orig.trim(), sprog);
    const ny = ov ?? orig;
    if (ny !== v) el.setAttribute(a, ny);
    alle[a] = { orig, vist: ny }; attrSpor.set(el, alle);
  }
}
function gennemgaa(rod: Node) {
  if (rod.nodeType === 3) { if (!undtaget(rod.parentElement)) behandlTekst(rod as Text); return; }
  if (rod.nodeType !== 1) return;
  const el = rod as Element;
  if (undtaget(el)) return;
  behandlAttr(el);
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) => (n.nodeType === 1 && undtaget(n as Element) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let n = w.nextNode(); n; n = w.nextNode()) { if (n.nodeType === 3) behandlTekst(n as Text); else behandlAttr(n as Element); }
}

/** Sæt sproget for hele siden. Dansk = original tekst. */
export async function saetSidesprog(s: Sprog) {
  if (s !== 'da') await hentOrdbog();
  sprog = s;
  if (typeof document === 'undefined') return;
  if (!observer) {
    observer = new MutationObserver((liste) => {
      if (sprog === 'da' && !liste.some((m) => m.type === 'characterData' && tekstSpor.has(m.target as Text))) return;
      for (const m of liste) {
        if (m.type === 'characterData') { if (!undtaget(m.target.parentElement)) behandlTekst(m.target as Text); }
        else if (m.type === 'attributes') { if (!undtaget(m.target as Element)) behandlAttr(m.target as Element); }
        else m.addedNodes.forEach((n) => gennemgaa(n));
      }
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...ATTR] });
  }
  gennemgaa(document.body);
}
